import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

/**
 * The handover block shown once the backend has REFUSED to remove an
 * instructor because they are still on the schedule.
 *
 * Extracted from InstructorsPage so every surface that can remove an
 * instructor shows the identical thing. Studio profile → Staff and the /staff
 * page had no handover UI at all: the operator saw "Choose who takes them
 * over" as a red error string with no control to do it with. Rebuilding the
 * block there would have produced a second, slightly different dialog for the
 * same decision — this is that block, not a copy of it.
 *
 * Renders nothing on its own: the caller supplies the surrounding Dialog, its
 * title and its buttons, because "disable", "delete staff" and "remove" each
 * word those differently while the handover itself is the same.
 */
export interface InstructorFutureWork {
  sessionCount: number;
  classCount: number;
  eventCount: number;
  wellnessPoolCount: number;
  serviceCount: number;
  bookingCount: number;
  memberCount: number;
  nextSessionAt: string | null;
}

interface Props {
  futureWork: InstructorFutureWork;
  /** Active instructors who could take the work on. */
  replacementOptions: { _id: string; name: string }[];
  replacementId: string;
  onReplacementChange: (id: string) => void;
  notifyMembers: boolean;
  onNotifyMembersChange: (value: boolean) => void;
}

const InstructorHandoverFields: React.FC<Props> = ({
  futureWork,
  replacementOptions,
  replacementId,
  onReplacementChange,
  notifyMembers,
  onNotifyMembersChange,
}) => (
  <Box mt={2}>
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Still attached to:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {([
          [futureWork.sessionCount, 'upcoming session', 'upcoming sessions'],
          [futureWork.classCount, 'class', 'classes'],
          [futureWork.eventCount, 'event', 'events'],
          [
            futureWork.wellnessPoolCount + futureWork.serviceCount,
            'wellness listing', 'wellness listings',
          ],
          [futureWork.bookingCount, 'member booking', 'member bookings'],
        ] as [number, string, string][])
          .filter(([n]) => n > 0)
          .map(([n, one, many]) => (
            <li key={many}>
              <strong>{n}</strong> {n === 1 ? one : many}
            </li>
          ))}
      </Box>
      {futureWork.bookingCount > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
          {futureWork.memberCount > 0
            ? `${futureWork.memberCount} ${futureWork.memberCount === 1 ? 'member has' : 'members have'} already booked those — they will see a different name on a class they paid for.`
            : 'Members have already booked those — they will see a different name on a class they paid for.'}
        </Typography>
      )}
      {futureWork.nextSessionAt && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          Next one:{' '}
          {new Date(futureWork.nextSessionAt).toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short',
          })}
          . Past classes keep their name and are not touched.
        </Typography>
      )}
    </Alert>

    <FormControl fullWidth size="small">
      <InputLabel>Who takes these over?</InputLabel>
      <Select
        label="Who takes these over?"
        value={replacementId}
        onChange={(e) => onReplacementChange(e.target.value)}
      >
        {replacementOptions.length === 0 && (
          <MenuItem value="" disabled>
            No other active instructor at this studio
          </MenuItem>
        )}
        {replacementOptions.map((i) => (
          <MenuItem key={i._id} value={i._id}>{i.name}</MenuItem>
        ))}
      </Select>
    </FormControl>

    {futureWork.bookingCount > 0 && (
      <FormControlLabel
        sx={{ mt: 1.5, alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={notifyMembers}
            onChange={(e) => onNotifyMembersChange(e.target.checked)}
            sx={{ mt: -0.75 }}
          />
        }
        label={
          <Box>
            <Typography variant="body2">
              Tell{' '}
              {futureWork.memberCount > 0
                ? `${futureWork.memberCount} affected ${futureWork.memberCount === 1 ? 'member' : 'members'}`
                : 'affected members'}{' '}
              about the change
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              One push and email each, naming the new instructor. Their
              bookings are not changed. Sent only after the handover
              succeeds.
            </Typography>
          </Box>
        }
      />
    )}
  </Box>
);

export default InstructorHandoverFields;
